import type { RowObject } from '../../components/datatable/types/RowObject';

export enum Gevo {
  /** Kautionsvertrag erstellt */
  VER_EST = 'VER_EST',
  /** Kautionsvertrag geprüft */
  VER_BSD = 'VER_BSD',

  // Aus BürgschaftService

  /** Bürgschaft erstellen */
  BRG_ERST = 'BRG_ERST',
  /** Bürgschaft akzeptiert */
  BRG_AKZ = 'BRG_AKZ',
  /** Bürgschaft abgelehnt */
  BRG_ABG = 'BRG_ABG',
  /** Bürgschaft korrigiert */
  BRG_KOR = 'BRG_KOR',
  /** Bürgschaft teilenthaftet */
  BRG_TEI = 'BRG_TEI',
  /** Bürgschaft zurückgegeben */
  BRG_RUE = 'BRG_RUE',
  /** Bürgschaft inanspruchgenommen */
  BRG_INA = 'BRG_INA',

  /** Bürgschaftsauftrag eingegangen */
  AUF_EIN = 'AUF_EIN',

  // Aus DokumentService

  /** Bürgschaftsdokument manuell erstellt */
  DOK_MAN = 'DOK_MAN',
  /** Bürgschaftsdokument systematisch erstellt */
  DOK_SYS = 'DOK_SYS',
  /** Auftraggebertext geprüft */
  AGT_BSD = 'AGT_BSD',

  /** Bürgschaft eingegangen */
  BRG_EIN = 'BRG_EIN',
  /** Bürgschaft prüfen */
  BRG_PRF = 'BRG_PRF',
  /** Bürgschaft enthaften */
  BRG_ENT = 'BRG_ENT',
  /** Statuswechsel einer Bürgschaft eingegangen */
  BRG_STA = 'BRG_STA',
}

export enum GevoKanal {
  SST = 'SST',
  EVS = 'EVS',
  MAIL = 'MAIL',
  PLA = 'PLA',
  PSST = 'PSST',
  KVER = 'KVER',
}

export const GevoKanalAnzeigeMap: Map<GevoKanal, string> = new Map([
  [GevoKanal.SST, 'Schnittstelle'],
  [GevoKanal.EVS, 'Eventsystem'],
  [GevoKanal.MAIL, 'E-Mail'],
  [GevoKanal.PLA, 'Plattform'],
  [GevoKanal.PSST, 'Plattform + Schnittstelle'],
  [GevoKanal.KVER, 'Keine Verwendung'],
]);

export const GevoKanalAnzeigeMapReverse: Map<string, GevoKanal> = new Map([
  ['Schnittstelle', GevoKanal.SST],
  ['Eventsystem', GevoKanal.EVS],
  ['E-Mail', GevoKanal.MAIL],
  ['Plattform', GevoKanal.PLA],
  ['Plattform + Schnittstelle', GevoKanal.PSST],
  ['Keine Verwendung', GevoKanal.KVER],
]);

export const GevoKanalAuftraggeberAnzeigeMap: Map<GevoKanal, string> = new Map([
  [GevoKanal.PLA, 'Plattform'],
  [GevoKanal.SST, 'Schnittstelle'],
  [GevoKanal.PSST, 'Plattform + Schnittstelle'],
  [GevoKanal.KVER, 'Keine Verwendung'],
]);

export enum GevoKanalAuspraegung {
  AGT_EIN_PLA_TEXTERSTELLUNG = 'AGT_EIN_PLA_TEXTERSTELLUNG',
  AGT_EIN_PLA_ERSTVERWENDUNG = 'AGT_EIN_PLA_ERSTVERWENDUNG',
}

export enum PruefprinzipAuspraegung {
  ZWEI_AUGEN = 'ZWEI_AUGEN',
  VIER_AUGEN = 'VIER_AUGEN',
}

export const PruefprinzipAuspraegungAnzeigeMap: Map<PruefprinzipAuspraegung, string> = new Map([
  [PruefprinzipAuspraegung.ZWEI_AUGEN, '2-Augen-Prinzip'],
  [PruefprinzipAuspraegung.VIER_AUGEN, '4-Augen-Prinzip'],
]);

export const PruefprinzipAuspraegungAnzeigeMapReverse: Map<string, PruefprinzipAuspraegung> =
  new Map([
    ['2-Augen-Prinzip', PruefprinzipAuspraegung.ZWEI_AUGEN],
    ['4-Augen-Prinzip', PruefprinzipAuspraegung.VIER_AUGEN],
  ]);

export const GevoKanalAuspraegungAnzeigeMap: Map<GevoKanalAuspraegung, string> = new Map([
  [GevoKanalAuspraegung.AGT_EIN_PLA_TEXTERSTELLUNG, 'Bei Texterstellung'],
  [GevoKanalAuspraegung.AGT_EIN_PLA_ERSTVERWENDUNG, 'Bei Erstverwendung'],
]);

export const GevoKanalAuspraegungAnzeigeMapReverse: Map<string, GevoKanalAuspraegung> = new Map([
  ['Bei Texterstellung', GevoKanalAuspraegung.AGT_EIN_PLA_TEXTERSTELLUNG],
  ['Bei Erstverwendung', GevoKanalAuspraegung.AGT_EIN_PLA_ERSTVERWENDUNG],
]);

export interface Kommunikation {
  gevoDto: Gevo;
  gevoKanalDto: GevoKanal;
  gevoKanalAuspraegungDto?: GevoKanalAuspraegung;
  pruefprinzipAuspraegungDto?: PruefprinzipAuspraegung;
  gevoEmail?: string;
  gevoLabel: string;
  gevoKanalListeDto?: GevoKanal[];
  gevoKanalAuspraegungMapDto?: Record<keyof typeof GevoKanal, GevoKanalAuspraegung[]>;
  pruefprinzipAuspraegungMapDto?: Record<keyof typeof GevoKanal, PruefprinzipAuspraegung[]>;
  allowedAlternativeText?: boolean | null;
  allowedIndividualText?: boolean | null;
}

export function createEmptyKommunikation(): Kommunikation {
  return {
    gevoDto: null,
    gevoKanalDto: null,
    gevoKanalAuspraegungDto: null,
    pruefprinzipAuspraegungDto: null,
    gevoEmail: '',
    gevoLabel: '',
    gevoKanalListeDto: null,
    gevoKanalAuspraegungMapDto: null,
    pruefprinzipAuspraegungMapDto: null,
    allowedAlternativeText: null,
    allowedIndividualText: null,
  };
}

export interface KommunikationAenderung {
  gevo: Gevo;
  gevoKanal: GevoKanal;
  gevoKanalAuspraegung?: GevoKanalAuspraegung;
  pruefprinzipAuspraegung?: PruefprinzipAuspraegung;
  email?: string;
  allowedAlternativeText: boolean | null;
  allowedIndividualText: boolean | null;
}

export interface GevoRowItem extends RowObject {
  gevoDto: Gevo;
  gevoName: string;
  kommunikationskanal: string;
}

export interface GevoDaten {
  gevoDefaultEmail?: string;
  gevos: GevoRowItem[];
}
